import { useQuery } from "@tanstack/react-query";
import { useServerTable, type AppColumns } from "../../../components/table/tableFeatures";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "@reactuses/core";
import { MdRotateLeft } from "react-icons/md";
import { css } from "../../../../styled-system/css";
import { HStack, VStack, Wrap } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { useDates } from "../../../dates/DatesContext";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import { useResolvedLabels } from "../../../i18n/useResolvedLabels";
import {
  AD_STATES,
  AD_STATUSES,
  AD_TYPES,
  ALGORITHM_OPTIONS,
  type AlgorithmValue,
  AMAZON_BUSINESS_MULTIPLIER_SUPPORT_MARKETPLACES,
  AUDIENCE_SEGMENT_TYPES,
} from "../../../components/core/blocks/AdPlan/constants";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { BulkActionButton, type BulkActionOptions } from "../../../components/table/BulkActionButton";
import { useBlockWindowQuery } from "../../../components/table/useBlockWindowQuery";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import {
  type ManagedTableState,
  type ManagedTableStateSetters,
} from "../../../components/table/usePersistedTableState";
import { resetTable, selectionStateToSelectedIds } from "../../../components/table/utils";
import { useCategoriesQuery } from "../../../hooks/useCategories";
import AdvertisementExpander from "../../advertising/components/AdvertisementExpander";
import { RT8FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import { DataViewPicker } from "../../dataViews/components/DataViewPicker";
import { useBulkUpdateAdState } from "../../advertising/api/useBulkUpdateAdState";
import { DownloadV4Report } from "../DownloadReport";
import { useGetV4AdvReportByAd, type AdvertisementQueryV4Row } from "../api/ads/v4AdvReportByAd";
import { useV4AdvReportExport } from "../api/ads/v4AdvReportExport";
import { useGetV4AdvReportOptions } from "../api/ads/v4AdvReportOptions";
import { EditableTableWrapper, EditableTableSubmitArea } from "../../../components/table/EditableTableWrapper";
import { scopedBatch } from "../../../utils/formily";
import NiceModal from "@ebay/nice-modal-react";
import BulkAdPlanActionForm, { type BulkAdPlanActionFormData } from "../../advertising/components/BulkAdPlanActionForm";
import { BulkDaypartingModal } from "../../advertising/dayparting/components/BulkDaypartingModal";
import { BulkBidStrategyModal } from "../../advertising/components/BulkBidStrategyForm";
import { BulkMinMaxBidsModal } from "../../advertising/components/BulkMinMaxBidsModal";
import { BulkAddProductsToAdsModal } from "../../advertising/components/BulkAddProductsToAdsModal";
import BulkTagsForm from "../../../components/core/blocks/BulkTagsForm";
import { useBulkAdScheduledActionsUpdate } from "../../advertising/api/useBulkAdScheduledActionsUpdate";
import { useBulkUpdateAdPropertiesV2 } from "../../advertising/api/useBulkUpdateAdPropertiesV2";
import { useAppSelector } from "../../../redux/hooks";
import { useAcosOrRoas } from "../../../utils/acosOrRoas";
import { formatScheduledAction } from "../../advertising/components/ScheduledBudgetSummary";
import { CategoryField } from "../../../components/core/blocks/AdPlan/fields";
import moment from "moment";
import { formatCurrency, formatNumber, formatPercent } from "../../../utils/formatNumber";
import { errorToast } from "../../../utils/toasts";
import BulkAdPlanRulesForm from "../../advertising/components/BulkAdPlanRulesForm";
import { useBulkUpdateAdProperties } from "../../advertising/api/useBulkUpdateAdProperties";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { ButtonGroup } from "../../../components/core/basic/ButtonGroup";
import BulkAdPlanModifierForm, {
  INITIAL_MODIFIER_FORM_DATA,
  keysToIngoreModifierValidationsFor,
} from "../../advertising/components/BulkAdPlanModifierForm";
import BulkAdPlanAlgoForm from "../../advertising/components/BulkAdPlanAlgoForm";
import { type BulkAdPropertyUpdate } from "../../../zodios/apiDefinitions/api/ads";
import { NTM_STATUS_OPTIONS, servingStatusOptions } from "../../advertising/filters/adLifecycleFilters";
import { useBiddingPrograms } from "../api/useBiddingPrograms";
import { useTagFilterOptions } from "../../tags/api/useTagFilterOptions";
import { EntityTagsFormModal } from "../../tags/components/EntityTagsFormModal";
import { adTypesByChannel } from "../../advertising/components/adTypeSection/AdTypeSection";
import { type AdType } from "../../../views/AdvertisingDashboardV2";
import { Select } from "../../../components/fields/base/Select";
import Seasonality from "../../advertising/components/Seasonality";
import { DevErrorDisplay } from "../../../components/core/blocks/DevErrorDisplay";
import { useSimpleCan } from "../../../config/casl/casl";
import BulkPlacementModifierManagementForm from "../../advertising/components/BulkPlacementModifierManagementForm";
import BulkAutoNegationSettingsForm, {
  DEFAULT_AUTO_NEGATION_SETTINGS_FORM_DATA,
  type BulkAutoNegationSettingsFormData,
} from "../../advertising/components/BulkAutoNegationSettingsForm";

type EditableTableFields = {
  daily_budget?: number;
  target_acos?: number;
  max_acos?: number;
  target_roas?: number;
  min_roas?: number;
};

type BulkWeekdayPartingFormData = {
  week_parting_method?: number;
  week_parting_multipliers?: { weekday: number; multiplier: number }[];
};

export const getAdTypeFilterOptions = (
  channel: string,
  allMediaPlanTypesLabel: string = "All Media Plan Types",
): AdType[] => {
  const adTypes = adTypesByChannel[channel as keyof typeof adTypesByChannel];
  return [
    { value: "all", label: allMediaPlanTypesLabel },
    ...adTypes
      .map((adType: AdType) => ({
        value: adType.value,
        label: adType.label,
      }))
      .filter(
        (type: AdType) =>
          ![AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_DISPLAY.value].map(String).includes(String(type.value)),
      ),
  ];
};

export function AdReportTable({
  state,
  initialState,
  setters,
  columns,
  quickFilterValue,
  setQuickFilterValue,
  additionalFilters,
  adType,
  setAdType,
}: {
  state: ManagedTableState;
  initialState: ManagedTableState;
  columns: AppColumns<AdvertisementQueryV4Row>;
  setters: ManagedTableStateSetters;
  quickFilterValue: string;
  setQuickFilterValue: (value: string) => void;
  additionalFilters?: {
    adType?: string;
    categoryId?: string;
  };
  adType?: number | string | null;
  setAdType?: (adType: number | string | null) => void;
}) {
  const { t } = useTranslation(["perf_reports", "advertising"]);
  const fmt = useDateFormatter("monthDay");
  const { start, end } = useDates();
  const [destinationCategoryId, setDestinationCategoryId] = useState<number | null>(null);
  const [filledTags, setFilledTags] = useState<string[]>([]);
  const [bulkActionFormData, setBulkActionFormData] = useState<BulkAdPlanActionFormData>({
    actionUnit: "percent",
    actionType: null,
  });
  const userCanUpdateAds = useSimpleCan("update", "Ad");
  const userCanExportAds = useSimpleCan("export", "Ad");
  const [bulkAlgoFormData, setBulkAlgoFormData] = useState<{
    algorithm: AlgorithmValue;
    adType: number;
    salesVolumeControl: number;
  }>({
    algorithm: ALGORITHM_OPTIONS.ROAS_BOOST.value,
    adType: AD_TYPES.SPONSORED_PRODUCT.value,
    salesVolumeControl: 2,
  });
  const [bulkAutoNegationSettingsFormData, setBulkAutoNegationSettingsFormData] =
    useState<BulkAutoNegationSettingsFormData>(DEFAULT_AUTO_NEGATION_SETTINGS_FORM_DATA);
  const [weekdayPartingData, setWeekdayPartingData] = useState<BulkWeekdayPartingFormData>({
    week_parting_method: 0,
  });
  const [bulkModifierFormData, setBulkModifierFormData] = useState(INITIAL_MODIFIER_FORM_DATA);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const acosOrRoas = useAcosOrRoas();
  const hasNTMModuleEnabled = useModuleEnabled("NON_TRELLIS_MANAGED_ADS");
  const hasBulkAddProductsEnabled = useModuleEnabled("BULK_ADD_PRODUCTS_TO_ADS");
  const hasMultiOrgRulesModuleEnabled = useModuleEnabled("MULTI_ORG_RULES");
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  const debounceSearch = useDebounce(state.globalFilter, 500);
  const quickFilter = useMemo(() => {
    return quickFilterValue !== "all" ? [{ id: quickFilterValue, value: { op: "=", value: true } }] : [];
  }, [quickFilterValue]);
  const filters = useMemo(() => {
    const { adType, categoryId } = additionalFilters || {};
    const adTypeFilter = adType && adType !== "all" ? [{ id: "ad_type", value: { op: "=", value: adType } }] : [];
    const categoryFilter = categoryId ? [{ id: "category_id", value: { op: "=", value: categoryId } }] : [];
    return [...state.columnFilters, ...adTypeFilter, ...categoryFilter, ...quickFilter];
  }, [state.columnFilters, additionalFilters, quickFilter]);

  const adTypeFilterOptions = getAdTypeFilterOptions(
    channel,
    t(($) => $.perf_reports["All Media Plan Types"]),
  );
  const resolvedAdTypeFilterOptions = useResolvedLabels(adTypeFilterOptions, ["advertising"]);

  const byAdOptionsGetter = useGetV4AdvReportByAd();
  const advByAdReportQuery = useBlockWindowQuery(byAdOptionsGetter, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters,
    search: debounceSearch,
  });

  const quickFilterCountsQuery = useQuery(
    // Params here are initial table state. As long as the user didn't change pagination size or sorting, this won't result in a separate request.
    // If they did, this is still static and will only be requested once.
    byAdOptionsGetter({
      pagination: initialState.pagination,
      sorting: initialState.sorting,
      filters:
        additionalFilters?.categoryId ?
          [{ id: "category_id", value: { op: "=", value: additionalFilters.categoryId } }]
        : [],
      search: "",
    }),
  );

  const resetBulkActionsForm = () => {
    setBulkActionFormData({
      actionUnit: "percent",
      actionType: null,
    });
  };

  const advReportOptionsQuery = useQuery({ ...useGetV4AdvReportOptions(), enabled: !tagsEnhancedEnabled });
  const tagEnhancedOptions = useTagFilterOptions("ADVERTISEMENT", { enabled: tagsEnhancedEnabled });
  const exportMutation = useV4AdvReportExport(filters);
  const categoriesQuery = useCategoriesQuery();
  const biddingProgramOptionsQuery = useBiddingPrograms();

  const tagOptions: { value: string; label: string }[] = useMemo((): { value: string; label: string }[] => {
    const tags = advReportOptionsQuery.data?.data.tags ?? [];

    return [
      { value: "all", label: t(($) => $.perf_reports["All Tags"]) },
      ...tags.map((tag: string): { value: string; label: string } => ({
        value: tag,
        label: tag,
      })),
    ];
  }, [advReportOptionsQuery.data?.data.tags, t]);

  const biddingProgramOptions: { value: number | string; label: string }[] = useMemo((): {
    value: number | string;
    label: string;
  }[] => {
    const biddingPrograms =
      biddingProgramOptionsQuery.data?.results.map((program) => ({
        value: program.id,
        label:
          hasMultiOrgRulesModuleEnabled ?
            t(($) => $.perf_reports["{name} (Source: {org})"], { name: program.name, org: program.org.name })
          : program.name,
      })) ?? [];

    return [{ value: "all", label: t(($) => $.perf_reports["All Rules"]) }, ...biddingPrograms];
  }, [biddingProgramOptionsQuery.data?.results, hasMultiOrgRulesModuleEnabled, t]);

  const categoryOptions = useMemo(
    () => [
      { value: "all", label: t(($) => $.perf_reports["All Categories"]) },
      ...(categoriesQuery.data?.map((category) => {
        return {
          value: category.id.toString(),
          label: category.leaf_category_name,
        };
      }) ?? []),
    ],
    [categoriesQuery.data, t],
  );
  const table = useServerTable({
    data: advByAdReportQuery.data?.data,
    columns,
    alternateSortingEnabled: true,
    getRowCanExpand: () => true,
    getRowId: (row) => row.ad_id.toString(),
    enableRowSelection: (row) => {
      return !row.original.multi_marketplace;
    },
    rowCount: advByAdReportQuery.data?.count || 0,
    initialState: initialState,
    state: state,
    ...setters,
    meta: {
      showSkeleton: advByAdReportQuery.isFetching && (!advByAdReportQuery.data || advByAdReportQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        tags: tagOptions,
        "tags_enhanced.id": tagEnhancedOptions,
        category_id: categoryOptions,
        state: [
          { value: "all", label: t(($) => $.perf_reports["All States"]) },
          AD_STATES.ACTIVE,
          AD_STATES.PAUSE,
          AD_STATES.ENDED,
        ],
        ad_status: [
          { value: "all", label: t(($) => $.perf_reports["All Statuses"]) },
          ...Object.values(AD_STATUSES).filter((status) => status !== AD_STATUSES.GV_ADV_ARCHIVED),
        ],
        serving_status: servingStatusOptions,
        day_parting_method: [
          {
            value: "all",
            label: t(($) => $.perf_reports["All"]),
          },
          {
            value: true,
            label: t(($) => $.perf_reports["With Dayparting"]),
          },
          {
            value: false,
            label: t(($) => $.perf_reports["Without Dayparting"]),
          },
        ],
        ntm_enrollment_status: [
          {
            label: t(($) => $.perf_reports["All Statuses"]),
            value: "all",
          },
          ...NTM_STATUS_OPTIONS,
        ],
        has_active_scheduled_actions: [
          {
            value: true,
            label: t(($) => $.perf_reports["With Scheduled Actions"]),
          },
          {
            value: false,
            label: t(($) => $.perf_reports["Without Scheduled Actions"]),
          },
        ],
        program_id: biddingProgramOptions,
        bid_algo: [
          { label: t(($) => $.perf_reports["All Strategies"]), value: "all" },
          ...Object.values(ALGORITHM_OPTIONS).map((option) => ({
            label: option.label,
            value: option.value,
          })),
        ],
        ad_source: [
          {
            label: t(($) => $.perf_reports["All Sources"]),
            value: "all",
          },
          {
            value: "Trellis",
            label: t(($) => $.perf_reports["Trellis"]),
          },
          {
            value: "Imported",
            label: t(($) => $.perf_reports["Imported"]),
          },
        ],
      },
      getRowSelectionDisabledReason: (row) => {
        if (row.original.multi_marketplace) {
          return t(($) => $.perf_reports["This media is locked because it is part of a multi-marketplace campaign."]);
        }
        return undefined;
      },
    },
  });

  const updateStateMutation = useBulkUpdateAdState();
  const updateAdPropertiesMutation = useBulkUpdateAdProperties();
  const bulkUpdatePropertiesMutation = useBulkUpdateAdPropertiesV2();
  const updateScheduledActionsMutation = useBulkAdScheduledActionsUpdate();

  const updatePlanState = (state: "paused" | "active", selected: string[]) => {
    const ads = selected.map((id) => {
      const ad = advByAdReportQuery.data?.data.find((ad) => ad.ad_id.toString() === id);
      return {
        id: parseInt(id),
        name: ad?.ad_name ?? "",
      };
    });

    updateStateMutation.mutate({ ads, state });
  };

  const updatePlanBidModifiers = () => {
    const {
      adType,
      topOfSearchModifier,
      productPagesModifier,
      restOfSearchModifier,
      amazonBusinessModifier,
      sbProductDetailPageModifier,
      sbOtherPageModifier,
      sbHomePageModifier,
      audienceBidMultiplierTargetAudienceId,
      audienceBidMultiplierTargetAudienceName,
      audienceBidMultiplierTargetAudienceSegmentType,
      audienceBidMultiplier,
      updatePlacementModifiers,
      updateAudienceModifiers,
    } = bulkModifierFormData;

    const modifierUpdatesToSend = [];

    if (
      [AD_TYPES.SPONSORED_PRODUCT.value, AD_TYPES.SPONSORED_PRODUCT_AUTO.value].includes(adType) &&
      updatePlacementModifiers
    ) {
      const formattedPlacementModifiers = [
        {
          placement: "PLACEMENT_TOP",
          percentage: topOfSearchModifier,
        },
        {
          placement: "PLACEMENT_PRODUCT_PAGE",
          percentage: productPagesModifier,
        },
        {
          placement: "PLACEMENT_REST_OF_SEARCH",
          percentage: restOfSearchModifier,
        },
      ];
      if (AMAZON_BUSINESS_MULTIPLIER_SUPPORT_MARKETPLACES.includes(marketPlace.marketPlace)) {
        formattedPlacementModifiers.push({
          placement: "SITE_AMAZON_BUSINESS",
          percentage: amazonBusinessModifier,
        });
      }

      modifierUpdatesToSend.push({
        field: "bid_adjustments_by_placement",
        action: "set",
        value: {
          [adType]: formattedPlacementModifiers,
        },
      } as const);
    }

    if (
      [
        AD_TYPES.SPONSORED_PRODUCT.value,
        AD_TYPES.SPONSORED_PRODUCT_AUTO.value,
        AD_TYPES.SPONSORED_BRAND_V2.value,
      ].includes(adType) &&
      updateAudienceModifiers
    ) {
      const formattedShopperCohortBiddingModifiers =
        audienceBidMultiplierTargetAudienceId ?
          [
            {
              shopperCohortType: "AUDIENCE_SEGMENT",
              percentage: audienceBidMultiplier,
              audienceSegments: [
                {
                  name: audienceBidMultiplierTargetAudienceName,
                  audienceId: audienceBidMultiplierTargetAudienceId.toString(),
                  audienceSegmentType:
                    audienceBidMultiplierTargetAudienceSegmentType ?? AUDIENCE_SEGMENT_TYPES.SPONSORED_ADS_AMC,
                },
              ],
            },
          ]
        : [];

      modifierUpdatesToSend.push({
        field: "shopper_cohort_bidding",
        action: "set",
        value: {
          [adType]: formattedShopperCohortBiddingModifiers,
        },
      } as const);
    }

    if ([AD_TYPES.SPONSORED_BRAND_V2.value].includes(adType) && updatePlacementModifiers) {
      const formattedSponsoredBrandPlacementModifiers = [
        {
          placement: "HOME",
          percentage: sbHomePageModifier,
        },
        {
          placement: "DETAIL_PAGE",
          percentage: sbProductDetailPageModifier,
        },
        {
          placement: "OTHER",
          percentage: sbOtherPageModifier,
        },
      ];

      modifierUpdatesToSend.push({
        field: "bid_adjustments_by_placement",
        action: "set",
        value: {
          [adType]: formattedSponsoredBrandPlacementModifiers,
        },
      } as const);
    }

    bulkUpdatePropertiesMutation.mutate(
      {
        advertisements: selected.map((id) => parseInt(id)),
        updates: modifierUpdatesToSend,
      },
      {
        onSettled: () => setBulkModifierFormData(INITIAL_MODIFIER_FORM_DATA),
      },
    );
  };

  const scheduleBudgetUpdates = () => {
    for (let i = 0; i < selected.length; i++) {
      const { actionType, actionValue, actionUnit, startDate, endDate } = bulkActionFormData;

      const updateData = formatScheduledAction(
        parseInt(selected[i] as string),
        startDate,
        endDate,
        actionUnit,
        actionValue,
        actionType,
        channel,
      );

      updateScheduledActionsMutation.mutate({
        data: updateData,
        isLast: i === selected.length - 1,
      });
    }
  };

  const selected = selectionStateToSelectedIds(table.state.rowSelection);

  const validateBulkAcosUpdate = (
    field: "target_acos" | "max_acos",
    actionType: string,
    actionValue: number,
  ): string | null => {
    for (const id of selected) {
      const ad = advByAdReportQuery.data?.data.find((ad) => ad.ad_id.toString() === id);
      if (!ad) continue;

      // Calculate new value based on action type
      // Note: actionValue is already signed (positive for increase, negative for decrease)
      // For ACOS fields, backend treats "percent" and "fixed" the same - they add percentage points
      let newValue: number;
      const currentValue = ad[field];
      if (actionType === "specify") {
        newValue = actionValue;
      } else {
        newValue = currentValue + actionValue;
      }

      // Validate relationship
      if (field === "target_acos" && newValue >= ad.max_acos) {
        return t(
          ($) =>
            $.perf_reports[
              'Cannot set ACOS Range Low to {newValue} - must be less than ACOS Range High ({maxAcos}) for "{adName}"'
            ],
          {
            newValue: formatPercent(newValue / 100),
            maxAcos: formatPercent(ad.max_acos / 100),
            adName: ad.ad_name,
          },
        );
      }
      if (field === "max_acos" && newValue <= ad.target_acos) {
        return t(
          ($) =>
            $.perf_reports[
              'Cannot set ACOS Range High to {newValue} - must be greater than ACOS Range Low ({targetAcos}) for "{adName}"'
            ],
          {
            newValue: formatPercent(newValue / 100),
            targetAcos: formatPercent(ad.target_acos / 100),
            adName: ad.ad_name,
          },
        );
      }
    }
    return null;
  };

  const validateBulkRoasUpdate = (
    field: "target_roas" | "min_roas",
    actionType: string,
    actionValue: number,
  ): string | null => {
    for (const id of selected) {
      const ad = advByAdReportQuery.data?.data.find((ad) => ad.ad_id.toString() === id);
      if (!ad) continue;

      // Calculate new value based on action type (ROAS uses fixed values only)
      // Note: actionValue is already signed (positive for increase, negative for decrease)
      let newValue: number;
      const currentValue = ad[field];
      if (actionType === "specify") {
        newValue = actionValue;
      } else {
        newValue = currentValue + actionValue;
      }

      // Validate relationship (ROAS Range High should be > ROAS Range Low)
      if (field === "target_roas" && newValue <= ad.min_roas) {
        return t(
          ($) =>
            $.perf_reports[
              'Cannot set ROAS Range High to {newValue} - must be greater than ROAS Range Low ({minRoas}) for "{adName}"'
            ],
          {
            newValue: formatNumber(newValue),
            minRoas: formatNumber(ad.min_roas),
            adName: ad.ad_name,
          },
        );
      }
      if (field === "min_roas" && newValue >= ad.target_roas) {
        return t(
          ($) =>
            $.perf_reports[
              'Cannot set ROAS Range Low to {newValue} - must be less than ROAS Range High ({targetRoas}) for "{adName}"'
            ],
          {
            newValue: formatNumber(newValue),
            targetRoas: formatNumber(ad.target_roas),
            adName: ad.ad_name,
          },
        );
      }
    }
    return null;
  };

  const bulkActionOptions: BulkActionOptions<string>[] = [
    {
      group: t(($) => $.perf_reports["State"]),
      actions: [
        {
          label: (selected) =>
            t(($) => $.perf_reports["{count, plural, one {Pause Media Plan} other {Pause Media Plans}}"], {
              count: selected.length,
            }),
          action: (selected) => {
            if (selected === "all") return;
            updatePlanState("paused", selected);
          },
          confirmationMessage: (
            <p className={css({ textAlign: "center" })}>
              {t(($) => $.perf_reports["Are you sure you want to pause the selected media plans?"])}
            </p>
          ),
        },
        {
          label: (selected) =>
            t(($) => $.perf_reports["{count, plural, one {Enable Media Plan} other {Enable Media Plans}}"], {
              count: selected.length,
            }),
          action: (selected) => {
            if (selected === "all") return;
            updatePlanState("active", selected);
          },
          confirmationMessage: (
            <p className={css({ textAlign: "center" })}>
              {t(($) => $.perf_reports["Are you sure you want to enable the selected media plans?"])}
            </p>
          ),
        },
      ],
    },
    {
      group: t(($) => $.perf_reports["Performance Targets"]),
      actions: [
        acosOrRoas(
          {
            label: t(($) => $.perf_reports["Update Optimal ACOS Range Low"]),
            action: () => {
              if (!bulkActionFormData.actionType || !bulkActionFormData.actionValue) {
                return;
              }
              const validationError = validateBulkAcosUpdate(
                "target_acos",
                bulkActionFormData.actionType,
                bulkActionFormData.actionType === "decrease" ?
                  bulkActionFormData.actionValue * -1
                : bulkActionFormData.actionValue,
              );
              if (validationError) {
                errorToast({ message: validationError });
                return;
              }
              bulkUpdatePropertiesMutation.mutate(
                {
                  advertisements: selected.map((id) => parseInt(id)),
                  updates: [
                    {
                      field: "target_acos",
                      action:
                        bulkActionFormData.actionType === "specify" ? "set"
                        : bulkActionFormData.actionUnit === "percent" ? "percent"
                        : "fixed",
                      value:
                        bulkActionFormData.actionType === "decrease" ?
                          bulkActionFormData.actionValue * -1
                        : bulkActionFormData.actionValue,
                    },
                  ],
                },
                {
                  onSuccess: () => {
                    resetBulkActionsForm();
                  },
                },
              );
            },
            confirmationMessage: (
              <p className={`fs-4 py-2 ${css({ textAlign: "center" })}`}>
                {t(
                  ($) =>
                    $.perf_reports["Are you sure you want to update the target ACOS for the selected media plans?"],
                )}
              </p>
            ),
            actionFormFields: (
              <BulkAdPlanActionForm
                formData={bulkActionFormData}
                setFormData={setBulkActionFormData}
                actionTarget={t(($) => $.perf_reports["Optimal ACOS Range Low"])}
                hideCurrencyOption={true}
                forcePercentOption={true}
              />
            ),
          },
          {
            label: t(($) => $.perf_reports["Update Optimal ROAS Range High"]),
            action: () => {
              if (!bulkActionFormData.actionType || !bulkActionFormData.actionValue) {
                return;
              }
              const validationError = validateBulkRoasUpdate(
                "target_roas",
                bulkActionFormData.actionType,
                bulkActionFormData.actionType === "decrease" ?
                  bulkActionFormData.actionValue * -1
                : bulkActionFormData.actionValue,
              );
              if (validationError) {
                errorToast({ message: validationError });
                return;
              }
              bulkUpdatePropertiesMutation.mutate(
                {
                  advertisements: selected.map((id) => parseInt(id)),
                  updates: [
                    {
                      field: "target_roas",
                      action: bulkActionFormData.actionType === "specify" ? "set" : "fixed",
                      value:
                        bulkActionFormData.actionType === "decrease" ?
                          bulkActionFormData.actionValue * -1
                        : bulkActionFormData.actionValue,
                    },
                  ],
                },
                {
                  onSuccess: () => {
                    resetBulkActionsForm();
                  },
                },
              );
            },
            confirmationMessage: (
              <p className={`fs-4 py-2 ${css({ textAlign: "center" })}`}>
                {t(
                  ($) =>
                    $.perf_reports["Are you sure you want to update the target ROAS for the selected media plans?"],
                )}
              </p>
            ),
            actionFormFields: (
              <BulkAdPlanActionForm
                formData={bulkActionFormData}
                setFormData={setBulkActionFormData}
                actionTarget={t(($) => $.perf_reports["Optimal ROAS Range High"])}
                hideCurrencyOption={true}
                hidePercentOption={true}
              />
            ),
          },
        ),
        acosOrRoas(
          {
            label: t(($) => $.perf_reports["Update Optimal ACOS Range High"]),
            action: () => {
              if (!bulkActionFormData.actionType || !bulkActionFormData.actionValue) {
                return;
              }
              const validationError = validateBulkAcosUpdate(
                "max_acos",
                bulkActionFormData.actionType,
                bulkActionFormData.actionType === "decrease" ?
                  bulkActionFormData.actionValue * -1
                : bulkActionFormData.actionValue,
              );
              if (validationError) {
                errorToast({ message: validationError });
                return;
              }
              bulkUpdatePropertiesMutation.mutate(
                {
                  advertisements: selected.map((id) => parseInt(id)),
                  updates: [
                    {
                      field: "max_acos",
                      action:
                        bulkActionFormData.actionType === "specify" ? "set"
                        : bulkActionFormData.actionUnit === "percent" ? "percent"
                        : "fixed",
                      value:
                        bulkActionFormData.actionType === "decrease" ?
                          bulkActionFormData.actionValue * -1
                        : bulkActionFormData.actionValue,
                    },
                  ],
                },
                {
                  onSuccess: () => {
                    resetBulkActionsForm();
                  },
                },
              );
            },
            confirmationMessage: (
              <p className={`fs-4 py-2 ${css({ textAlign: "center" })}`}>
                {t(
                  ($) =>
                    $.perf_reports["Are you sure you want to update the maximum ACOS for the selected media plans?"],
                )}
              </p>
            ),
            actionFormFields: (
              <BulkAdPlanActionForm
                formData={bulkActionFormData}
                setFormData={setBulkActionFormData}
                actionTarget={t(($) => $.perf_reports["Optimal ACOS Range High"])}
                hideCurrencyOption={true}
                forcePercentOption={true}
              />
            ),
          },
          {
            label: t(($) => $.perf_reports["Update Optimal ROAS Range Low"]),
            action: () => {
              if (!bulkActionFormData.actionType || !bulkActionFormData.actionValue) {
                return;
              }
              const validationError = validateBulkRoasUpdate(
                "min_roas",
                bulkActionFormData.actionType,
                bulkActionFormData.actionType === "decrease" ?
                  bulkActionFormData.actionValue * -1
                : bulkActionFormData.actionValue,
              );
              if (validationError) {
                errorToast({ message: validationError });
                return;
              }
              bulkUpdatePropertiesMutation.mutate(
                {
                  advertisements: selected.map((id) => parseInt(id)),
                  updates: [
                    {
                      field: "min_roas",
                      action: bulkActionFormData.actionType === "specify" ? "set" : "fixed",
                      value:
                        bulkActionFormData.actionType === "decrease" ?
                          bulkActionFormData.actionValue * -1
                        : bulkActionFormData.actionValue,
                    },
                  ],
                },
                {
                  onSuccess: () => {
                    resetBulkActionsForm();
                  },
                },
              );
            },
            confirmationMessage: (
              <p className={`fs-4 py-2 ${css({ textAlign: "center" })}`}>
                {t(
                  ($) =>
                    $.perf_reports["Are you sure you want to update the minimum ROAS for the selected media plans?"],
                )}
              </p>
            ),
            actionFormFields: (
              <BulkAdPlanActionForm
                formData={bulkActionFormData}
                setFormData={setBulkActionFormData}
                actionTarget={t(($) => $.perf_reports["Optimal ROAS Range Low"])}
                hideCurrencyOption={true}
                hidePercentOption={true}
              />
            ),
          },
        ),
      ],
    },
    {
      group: t(($) => $.perf_reports["Budget"]),
      actions: [
        {
          label: t(($) => $.perf_reports["Update Budget"]),
          action: () => {
            if (!bulkActionFormData.actionType || !bulkActionFormData.actionValue) {
              return;
            }
            bulkUpdatePropertiesMutation.mutate(
              {
                advertisements: selected.map((id) => parseInt(id)),
                updates: [
                  {
                    field: "daily_budget",
                    action:
                      bulkActionFormData.actionType === "specify" ? "set"
                      : bulkActionFormData.actionUnit === "percent" ? "percent"
                      : "fixed",
                    value:
                      bulkActionFormData.actionType === "decrease" ?
                        bulkActionFormData.actionValue * -1
                      : bulkActionFormData.actionValue,
                  },
                ],
              },
              {
                onSuccess: () => {
                  resetBulkActionsForm();
                },
              },
            );
          },
          confirmationMessage: (
            <p className={`fs-4 py-2 ${css({ textAlign: "center" })}`}>
              {t(
                ($) => $.perf_reports["Are you sure you want to update the daily budget for the selected media plans?"],
              )}
            </p>
          ),
          actionFormFields: (
            <BulkAdPlanActionForm
              formData={bulkActionFormData}
              setFormData={setBulkActionFormData}
              actionTarget={t(($) => $.perf_reports["Daily Budget"])}
              hidePercentOption={bulkActionFormData.actionType === "specify"}
            />
          ),
        },
        {
          label: t(($) => $.perf_reports["Schedule Budget Updates"]),
          action: () => scheduleBudgetUpdates(),
          confirmationMessage: (
            <p className={`fs-4 py-2 ${css({ textAlign: "center" })}`}>
              {(
                !bulkActionFormData.actionValue ||
                !bulkActionFormData.actionType ||
                !bulkActionFormData.actionUnit ||
                !bulkActionFormData.startDate ||
                !bulkActionFormData.endDate
              ) ?
                t(($) => $.perf_reports["Please fill out all required fields."])
              : t(
                  ($) =>
                    $.perf_reports[
                      "Are you sure you want to {actionType} the daily budget by {amount} for the selected media plans from {startDate} to {endDate}  ({hours} hours)?"
                    ],
                  {
                    actionType: bulkActionFormData.actionType,
                    amount:
                      bulkActionFormData.actionUnit === "percent" ?
                        `${Math.round(Math.abs(bulkActionFormData.actionValue))}%`
                      : formatCurrency(Math.abs(bulkActionFormData.actionValue), {
                          marketPlace: marketPlace.marketPlace,
                        }),
                    startDate: fmt.format(moment(bulkActionFormData.startDate).toDate()),
                    endDate: fmt.format(moment(bulkActionFormData.endDate).toDate()),
                    hours: (moment(bulkActionFormData.endDate).diff(bulkActionFormData.startDate, "days") + 1) * 24,
                  },
                )
              }
            </p>
          ),
          actionFormFields: (
            <BulkAdPlanActionForm
              formData={bulkActionFormData}
              setFormData={setBulkActionFormData}
              actionTarget={t(($) => $.perf_reports["Daily Budget"])}
              schedulingDialogue={true}
            />
          ),
          disableConfirmButton:
            !bulkActionFormData.actionValue ||
            !bulkActionFormData.actionType ||
            !bulkActionFormData.actionUnit ||
            !bulkActionFormData.startDate ||
            !bulkActionFormData.endDate,
        },
        {
          label: t(($) => $.perf_reports["Cancel Scheduled Budget Updates"]),
          action: (selected) => {
            if (selected === "all") return;
            bulkUpdatePropertiesMutation.mutate({
              advertisements: selected.map((id) => parseInt(id)),
              updates: [
                {
                  field: "cancel_scheduled_actions",
                  action: "set",
                  value: "cancelled",
                },
              ],
            });
          },
          confirmationMessage: (
            <p className="fs-4 py-2">
              {t(
                ($) =>
                  $.perf_reports[
                    "{count, plural, one {Are you sure you want to cancel scheduled budget updates for the selected media plan?} other {Are you sure you want to cancel scheduled budget updates for the selected media plans?}}"
                  ],
                { count: selected.length },
              )}
            </p>
          ),
        },
        {
          label: t(($) => $.perf_reports["Update Budget Programs"]),
          action: () => {
            if (bulkActionFormData.budgetProgram === undefined) {
              return;
            }

            bulkUpdatePropertiesMutation.mutate({
              advertisements: selected.map((id) => parseInt(id)),
              updates: [
                {
                  field: "budget_rules",
                  action: "set",
                  value: bulkActionFormData.budgetProgram,
                },
              ],
            });
          },
          actionFormFields: (
            <BulkAdPlanRulesForm
              formData={bulkActionFormData}
              setFormData={setBulkActionFormData}
              programType={"budget"}
            />
          ),
          confirmationMessage: (
            <p className={css({ textAlign: "center" })}>
              {bulkActionFormData.budgetProgram === undefined ?
                ""
              : t(
                  ($) =>
                    $.perf_reports[
                      "{count, plural, one {Are you sure you want to update the budget programs for the selected media plan?} other {Are you sure you want to update the budget programs for the selected media plans?}}"
                    ],
                  { count: selected.length },
                )
              }
            </p>
          ),
          disableConfirmButton: bulkActionFormData.budgetProgram === undefined,
          hidden: channel === "walmart",
        },
      ],
    },
    {
      group: t(($) => $.perf_reports["Categorization"]),
      actions: [
        {
          label: (selected) =>
            t(($) => $.perf_reports["{count, plural, one {Update Plan Category} other {Update Plan Categories}}"], {
              count: selected.length,
            }),
          action: () => {
            if (!destinationCategoryId) {
              return;
            }
            bulkUpdatePropertiesMutation.mutate(
              {
                advertisements: selected.map((id) => parseInt(id)),
                updates: [
                  {
                    field: "category",
                    action: "set",
                    value: destinationCategoryId,
                  },
                ],
              },
              {
                onSuccess: () => {
                  resetBulkActionsForm();
                },
              },
            );
          },
          confirmationMessage: (
            <p className={`fs-4 py-2 ${css({ textAlign: "center" })}`}>
              {t(
                ($) =>
                  $.perf_reports[
                    "{count, plural, one {Are you sure you want to update the category for the selected media plan?} other {Are you sure you want to update the category for the {count} selected media plans?}}"
                  ],
                { count: selected.length },
              )}
            </p>
          ),
          actionFormFields: (
            <div className="pb-5">
              <p className="pb-4 fs-4">
                {t(
                  ($) =>
                    $.perf_reports[
                      "{count, plural, one {Please specify the destination category you would like to move the selected media plan to.} other {Please specify the destination category you would like to move the selected media plans to.}}"
                    ],
                  { count: selected.length },
                )}
              </p>
              <div className="d-flex justify-content-center">
                <CategoryField
                  dashboard={true}
                  onChange={(value: { categoryId: number }) => {
                    setDestinationCategoryId(value.categoryId);
                  }}
                  state={{
                    updatedCategoryId: destinationCategoryId,
                  }}
                  channel={channel}
                  marketPlace={marketPlace}
                  existingAdCategorySelector={true}
                  fieldsToValidate={[]}
                  errors={{}}
                  inline={false}
                  review={false}
                  isDisabled={false}
                />
              </div>
            </div>
          ),
        },
        ...(tagsEnhancedEnabled ?
          [
            {
              label: t(($) => $.perf_reports["Add Media Plan Tags"]),
              action: () => {
                void NiceModal.show(EntityTagsFormModal, {
                  entityType: "ADVERTISEMENT" as const,
                  title: t(($) => $.perf_reports["Add Media Plan Tags"]),
                  onSubmit: async (tagIds: number[]) => {
                    await bulkUpdatePropertiesMutation.mutateAsync({
                      advertisements: selected.map((id) => parseInt(id)),
                      updates: [{ field: "tags", action: "add", value: tagIds }],
                    });
                  },
                });
              },
            },
            {
              label: t(($) => $.perf_reports["Remove Media Plan Tags"]),
              action: () => {
                void NiceModal.show(EntityTagsFormModal, {
                  entityType: "ADVERTISEMENT" as const,
                  title: t(($) => $.perf_reports["Remove Media Plan Tags"]),
                  onSubmit: async (tagIds: number[]) => {
                    await bulkUpdatePropertiesMutation.mutateAsync({
                      advertisements: selected.map((id) => parseInt(id)),
                      updates: [{ field: "tags", action: "remove", value: tagIds }],
                    });
                  },
                });
              },
            },
          ]
        : [
            {
              label: t(($) => $.perf_reports["Add Media Plan Tags"]),
              action: () => {
                bulkUpdatePropertiesMutation.mutate(
                  {
                    advertisements: selected.map((id) => parseInt(id)),
                    updates: [
                      {
                        field: "tags" as const,
                        action: "add" as const,
                        value: filledTags,
                      },
                    ],
                  },
                  { onSuccess: () => setFilledTags([]) },
                );
              },
              actionFormFields: (
                <BulkTagsForm
                  filledTags={filledTags}
                  setFilledTags={setFilledTags}
                  selected={selected}
                  suggestedTags={tagOptions.filter((tag) => tag.value !== "all").map((tag) => tag.value)}
                  taggedItemDescription={t(($) => $.perf_reports["media plan"])}
                  actionType={"add"}
                />
              ),
              confirmationMessage: (
                <p className={css({ textAlign: "center" })}>
                  {t(
                    ($) =>
                      $.perf_reports[
                        "{tagCount, plural, one {Are you sure you want to add this tag from the selected {planCount, plural, one {media plan} other {media plans}}?} other {Are you sure you want to add these tags from the selected {planCount, plural, one {media plan} other {media plans}}?}}"
                      ],
                    { tagCount: filledTags.length, planCount: selected.length },
                  )}
                </p>
              ),
              disableConfirmButton: !filledTags.length,
            },
            {
              label: t(($) => $.perf_reports["Remove Media Plan Tags"]),
              action: () => {
                bulkUpdatePropertiesMutation.mutate(
                  {
                    advertisements: selected.map((id) => parseInt(id)),
                    updates: [
                      {
                        field: "tags" as const,
                        action: "remove" as const,
                        value: filledTags,
                      },
                    ],
                  },
                  { onSuccess: () => setFilledTags([]) },
                );
              },
              actionFormFields: (
                <BulkTagsForm
                  filledTags={filledTags}
                  setFilledTags={setFilledTags}
                  selected={selected}
                  suggestedTags={tagOptions.filter((tag) => tag.value !== "all").map((tag) => tag.value)}
                  taggedItemDescription={t(($) => $.perf_reports["media plan"])}
                  actionType={"remove"}
                />
              ),
              confirmationMessage: (
                <p className={css({ textAlign: "center" })}>
                  {t(
                    ($) =>
                      $.perf_reports[
                        "{tagCount, plural, one {Are you sure you want to remove this tag from the selected {planCount, plural, one {media plan} other {media plans}}?} other {Are you sure you want to remove these tags from the selected {planCount, plural, one {media plan} other {media plans}}?}}"
                      ],
                    { tagCount: filledTags.length, planCount: selected.length },
                  )}
                </p>
              ),
              disableConfirmButton: !filledTags.length,
            },
          ]),
      ],
    },
    {
      group: t(($) => $.perf_reports["Dayparting & Placement"]),
      actions: [
        {
          label: t(($) => $.perf_reports["Update Dayparting"]),
          action: () => {
            void NiceModal.show(BulkDaypartingModal, {
              advertisementIds: selected.map((id) => parseInt(id)),
              categoryId: additionalFilters?.categoryId ? parseInt(additionalFilters.categoryId) : undefined,
              startDate: start.toDate(),
              endDate: end.toDate(),
            });
          },
          hidden: channel === "walmart",
        },
        {
          label: t(($) => $.perf_reports["Update Weekday Parting"]),
          action: () => {
            if (weekdayPartingData.week_parting_method === undefined) {
              return;
            }
            bulkUpdatePropertiesMutation.mutate(
              {
                advertisements: selected.map((id) => parseInt(id)),
                updates: [
                  {
                    field: "week_parting_method",
                    action: "set",
                    value: weekdayPartingData.week_parting_method,
                  },
                  {
                    field: "week_parting_multipliers",
                    action: "set",
                    value: weekdayPartingData.week_parting_multipliers ?? null,
                  },
                ],
              },
              {
                onSuccess() {
                  setWeekdayPartingData({ week_parting_method: 0 });
                },
              },
            );
          },
          actionFormFields: (
            <Seasonality
              adPlanParams={{ weekPartingMethod: 0, weekPartingMultipliers: null }}
              isBulkActionForm={true}
              adPlanData={{}}
              categoryId={additionalFilters?.categoryId}
              marketPlace={marketPlace}
              customOnChange={(newValues: Partial<BulkWeekdayPartingFormData>) => {
                setWeekdayPartingData((prev) => ({ ...prev, ...newValues }));
              }}
            />
          ),
          confirmationMessage: (
            <p className={css({ textAlign: "center" })}>
              {bulkActionFormData.budgetProgram === undefined ?
                ""
              : t(
                  ($) =>
                    $.perf_reports[
                      "{count, plural, one {Are you sure you want to update weekday parting for the selected media plan?} other {Are you sure you want to update weekday parting for the selected media plans?}}"
                    ],
                  { count: selected.length },
                )
              }
            </p>
          ),
          hidden: channel === "walmart",
        },
        {
          label: t(($) => $.perf_reports["Update Placement Multiplier Automation"]),
          action: () => {
            const { algoManagedPlacementMultipliers } = bulkModifierFormData;
            const placementMultiplierManagementUpdates = [
              {
                field: "algo_managed_placement_multipliers",
                action: "set",
                value: algoManagedPlacementMultipliers,
              },
            ] as BulkAdPropertyUpdate["updates"];

            bulkUpdatePropertiesMutation.mutate(
              {
                advertisements: selected.map((id) => parseInt(id)),
                updates: placementMultiplierManagementUpdates,
              },
              {
                onSettled: () => setBulkModifierFormData(INITIAL_MODIFIER_FORM_DATA),
              },
            );
          },
          actionFormFields: (
            <BulkPlacementModifierManagementForm
              formData={bulkModifierFormData}
              setFormData={setBulkModifierFormData}
            />
          ),
          confirmationMessage: (
            <p className={css({ textAlign: "center" })}>
              {t(
                ($) =>
                  $.perf_reports[
                    "{count, plural, one {Are you sure you want to update placement multiplier management for the selected media plan?} other {Are you sure you want to update placement multiplier management for the selected media plans?}}"
                  ],
                { count: selected.length },
              )}
            </p>
          ),
          hidden: channel === "walmart",
        },
      ],
    },
    {
      group: t(($) => $.perf_reports["Bidding"]),
      actions: [
        {
          label: t(($) => $.perf_reports["Update Bidding Programs"]),
          action: () => {
            if (bulkActionFormData.biddingProgram === undefined) {
              return;
            }
            bulkUpdatePropertiesMutation.mutate({
              advertisements: selected.map((id) => parseInt(id)),
              updates: [
                {
                  field: "bidding_rules",
                  action: "set",
                  value: bulkActionFormData.biddingProgram,
                },
              ],
            });
          },
          actionFormFields: (
            <BulkAdPlanRulesForm
              formData={bulkActionFormData}
              setFormData={setBulkActionFormData}
              programType={"bidding"}
            />
          ),
          confirmationMessage: (
            <p className={css({ textAlign: "center" })}>
              {bulkActionFormData.biddingProgram === undefined ?
                ""
              : t(
                  ($) =>
                    $.perf_reports[
                      "{count, plural, one {Are you sure you want to update the bidding programs for the selected media plan?} other {Are you sure you want to update the bidding programs for the selected media plans?}}"
                    ],
                  { count: selected.length },
                )
              }
            </p>
          ),
          disableConfirmButton: bulkActionFormData.biddingProgram === undefined,
          hidden: channel === "walmart",
        },
        {
          label: t(($) => $.perf_reports["Update Bidding Algorithm"]),
          action: () => {
            const { algorithm, salesVolumeControl, adType } = bulkAlgoFormData;

            const algoUpdates = [
              {
                field: "bid_algo",
                action: "set",
                value: { [adType]: algorithm === ALGORITHM_OPTIONS.RULES_ENGINE_ONLY.value ? null : algorithm },
              },
            ] as BulkAdPropertyUpdate["updates"];

            if (algorithm === ALGORITHM_OPTIONS.GROWTH_MAX.value) {
              algoUpdates.push({
                field: "sales_volume_control_slider",
                action: "set",
                value: { [adType]: salesVolumeControl },
              });
            }

            bulkUpdatePropertiesMutation.mutate({
              advertisements: selected.map((id) => parseInt(id)),
              updates: algoUpdates,
            });
          },
          actionFormFields: <BulkAdPlanAlgoForm formData={bulkAlgoFormData} setFormData={setBulkAlgoFormData} />,
          confirmationMessage: (
            <p>
              {t(
                ($) =>
                  $.perf_reports[
                    "{count, plural, one {Are you sure you want to update the bidding algorithm for the selected media plan?} other {Are you sure you want to update the bidding algorithm for the selected media plans?}}"
                  ],
                { count: selected.length },
              )}
            </p>
          ),
        },
        {
          label: t(($) => $.perf_reports["Update Bidding Modifiers"]),
          action: () => updatePlanBidModifiers(),
          confirmationMessage: (
            <p className="fs-4 py-2">
              {t(
                ($) =>
                  $.perf_reports[
                    "Are you sure you want to update the placement modifiers for the selected media plans?"
                  ],
              )}
            </p>
          ),
          actionFormFields: (
            <BulkAdPlanModifierForm formData={bulkModifierFormData} setFormData={setBulkModifierFormData} />
          ),
          disableConfirmButton: Object.entries(bulkModifierFormData)
            .filter(([key]) => !keysToIngoreModifierValidationsFor.includes(key))
            .map(([, value]) => value)
            .some((value: number | null) => {
              return value === null || isNaN(value) || value > 900 || value < 0;
            }),
          hidden: channel === "walmart",
        },
        {
          label: t(($) => $.perf_reports["Update Bidding Strategy"]),
          action: (selected) => {
            if (selected === "all") return;
            void NiceModal.show(BulkBidStrategyModal, {
              advertisementIds: selected.map((id) => parseInt(id)),
            });
          },
          confirmationMessage: null,
          hidden: channel === "walmart",
        },
        {
          label: t(($) => $.perf_reports["Update Min and Max Bids"]),
          action: (selected) => {
            if (selected === "all") return;
            void NiceModal.show(BulkMinMaxBidsModal, {
              advertisementIds: selected.map((id) => parseInt(id)),
            });
          },
          confirmationMessage: null,
        },
      ],
    },
    {
      group: t(($) => $.perf_reports["Automation"]),
      actions: [
        {
          label: t(($) => $.perf_reports["Update Auto Negation Settings"]),
          action: () => {
            const { autoNegationEnabled, maxAcosNegationThreshold, cumulativeNegationThreshold } =
              bulkAutoNegationSettingsFormData;

            const autoNegationSettingsUpdates = [
              {
                field: "auto_negation",
                action: "set",
                value: autoNegationEnabled,
              },
              {
                field: "target_acos_negation_threshold",
                action: "set",
                value: autoNegationEnabled ? maxAcosNegationThreshold! / 100 : null,
              },
              {
                field: "cumm_threshold",
                action: "set",
                value: autoNegationEnabled ? cumulativeNegationThreshold! / 100 : null,
              },
            ] as BulkAdPropertyUpdate["updates"];

            bulkUpdatePropertiesMutation.mutate(
              {
                advertisements: selected.map((id) => parseInt(id)),
                updates: autoNegationSettingsUpdates,
              },
              {
                onSettled: () => setBulkAutoNegationSettingsFormData(DEFAULT_AUTO_NEGATION_SETTINGS_FORM_DATA),
              },
            );
          },
          actionFormFields: (
            <BulkAutoNegationSettingsForm
              formData={bulkAutoNegationSettingsFormData}
              setFormData={setBulkAutoNegationSettingsFormData}
            />
          ),
          confirmationMessage: (
            <p className={css({ textAlign: "center" })}>
              {t(
                ($) =>
                  $.perf_reports[
                    "{count, plural, one {Are you sure you want to update auto negation settings for the selected media plan?} other {Are you sure you want to update auto negation settings for the selected media plans?}}"
                  ],
                { count: selected.length },
              )}
            </p>
          ),
          hidden: channel === "walmart",
        },
        {
          label: t(($) => $.perf_reports["Immediately Send Updates to Amazon"]),
          action: (selected) => {
            if (selected === "all") return;
            bulkUpdatePropertiesMutation.mutate({
              advertisements: selected.map((id) => parseInt(id)),
              updates: [
                {
                  field: "push_to_amazon",
                  action: "set",
                  value: true,
                },
              ],
            });
          },
          confirmationMessage: (
            <p className={css({ textAlign: "center" })}>
              {t(
                ($) =>
                  $.perf_reports[
                    "{count, plural, one {Are you sure you want to immediately trigger optimizations for the selected media plan?} other {Are you sure you want to immediately trigger optimizations for the selected media plans?}}"
                  ],
                { count: selected.length },
              )}
            </p>
          ),
          hidden: channel === "walmart",
        },
      ],
    },
    {
      group: t(($) => $.perf_reports["Non-Trellis Managed Campaigns"]),
      actions: [
        {
          label: t(($) => $.perf_reports["Import Keywords and Targets"]),
          action: (selected) => {
            if (selected === "all") return;
            bulkUpdatePropertiesMutation.mutate({
              advertisements: selected.map((id) => parseInt(id)),
              updates: [
                {
                  field: "non_trellis_enrolled_ad",
                  action: "set",
                  value: true,
                },
              ],
            });
          },
          confirmationMessage: (
            <p className={css({ textAlign: "center" })}>
              {t(
                ($) =>
                  $.perf_reports[
                    "{count, plural, one {Are you sure you want to import targets and keywords for the selected media plan? Once they are imported you can enroll this campaign in Trellis to have it automatically managed by our AI.} other {Are you sure you want to import targets and keywords for the selected media plans? Once they are imported you can enroll this campaign in Trellis to have it automatically managed by our AI.}}"
                  ],
                { count: selected.length },
              )}
            </p>
          ),
          hidden: !hasNTMModuleEnabled,
        },
        {
          label: t(($) => $.perf_reports["Manage Imported Media Plans"]),
          action: (selected) => {
            if (selected === "all") return;
            bulkUpdatePropertiesMutation.mutate({
              advertisements: selected.map((id) => parseInt(id)),
              updates: [
                {
                  field: "non_trellis_enrolled_ad_accept_bids",
                  action: "set",
                  value: true,
                },
              ],
            });
          },
          confirmationMessage: (
            <p className={css({ textAlign: "center" })}>
              {t(
                ($) =>
                  $.perf_reports[
                    "{count, plural, one {Are you sure you want to enroll and automatically accept bids for the selected media plan?} other {Are you sure you want to enroll and automatically accept bids for the selected media plans?}}"
                  ],
                { count: selected.length },
              )}
            </p>
          ),
          hidden: !hasNTMModuleEnabled,
        },
        {
          label: t(($) => $.perf_reports["Unmanage Imported Media Plans"]),
          action: (selected) => {
            if (selected === "all") return;
            bulkUpdatePropertiesMutation.mutate({
              advertisements: selected.map((id) => parseInt(id)),
              updates: [
                {
                  field: "non_trellis_enrolled_ad_accept_bids",
                  action: "set",
                  value: false,
                },
              ],
            });
          },
          confirmationMessage: (
            <p className={css({ textAlign: "center" })}>
              {t(
                ($) =>
                  $.perf_reports[
                    "{count, plural, one {Are you sure you want to disable bid management for the selected media plan?} other {Are you sure you want to disable bid management for the selected media plans?}}"
                  ],
                { count: selected.length },
              )}
            </p>
          ),
          hidden: !hasNTMModuleEnabled,
        },
      ],
    },
    {
      group: "Products",
      actions: [
        {
          label: (selected) => `Add Products to Media Plan${selected.length > 1 ? "s" : ""}`,
          action: (selected) => {
            if (selected === "all") return;
            void NiceModal.show(BulkAddProductsToAdsModal, {
              advertisementIds: selected.map((id) => parseInt(id)),
              renderAdEligibilityWarning: true,
            });
          },
          confirmationMessage: null,
          hidden: !hasBulkAddProductsEnabled || channel !== "amazon",
        },
      ],
    },
  ];

  return (
    <EditableTableWrapper formOptions={{ readPretty: !userCanUpdateAds }}>
      <Wrap justifyContent={"space-between"} width={"100%"} padding={"small"} fontSize={"large"}>
        <HStack gap={"small"}>
          <ButtonGroup
            buttons={[
              {
                label: t(($) => $.perf_reports["All Media Plans"]),
                value: "all",
                counter: quickFilterCountsQuery.data?.count,
              },
              {
                label: t(($) => $.perf_reports["New"]),
                value: "recent",
                counter: quickFilterCountsQuery.data?.count_details.recent_count,
              },
              {
                label: t(($) => $.perf_reports["Trending Up"]),
                value: "trend_up",
                counter: quickFilterCountsQuery.data?.count_details.trend_up_count,
              },
              {
                label: t(($) => $.perf_reports["Trending Down"]),
                value: "trend_down",
                counter: quickFilterCountsQuery.data?.count_details.trend_down_count,
              },
              {
                label: acosOrRoas(
                  t(($) => $.perf_reports["High ACOS"]),
                  t(($) => $.perf_reports["Low ROAS"]),
                ),
                value: "high_acos",
                counter: quickFilterCountsQuery.data?.count_details.high_acos_count,
              },
              {
                label: acosOrRoas(
                  t(($) => $.perf_reports["Low ACOS"]),
                  t(($) => $.perf_reports["High ROAS"]),
                ),
                value: "low_acos",
                counter: quickFilterCountsQuery.data?.count_details.low_acos_count,
              },
              {
                label: acosOrRoas(
                  t(($) => $.perf_reports["ACOS in Range"]),
                  t(($) => $.perf_reports["ROAS in Range"]),
                ),
                value: "in_range_acos",
                counter: quickFilterCountsQuery.data?.count_details.in_range_acos_count,
              },
              {
                label: t(($) => $.perf_reports["Low Budget"]),
                value: "serving_low",
                counter: quickFilterCountsQuery.data?.count_details.serving_low_count,
              },
            ]}
            value={quickFilterValue}
            onSelect={setQuickFilterValue}
          />
        </HStack>
        {userCanUpdateAds && (
          <>
            <EditableTableSubmitArea
              onSubmit={async (values: Record<string, EditableTableFields>, form) => {
                const payload = Object.entries(values).map(([id, value]) => {
                  return {
                    id: parseInt(id),
                    ...value,
                  };
                });
                await updateAdPropertiesMutation.mutateAsync(payload, {
                  onSuccess() {
                    scopedBatch(() => {
                      form.setInitialValues(form.values);
                      void form.reset();
                    });
                  },
                });
              }}
            />
            <BulkActionButton isDisabled={!userCanUpdateAds} selected={selected} options={bulkActionOptions} />
          </>
        )}
      </Wrap>
      <div className={css({ width: "400px", paddingTop: "large", paddingBottom: "large" })}>
        <Select
          variant={"inline"}
          onSelectionChange={(value) => {
            setAdType?.(value);
          }}
          items={resolvedAdTypeFilterOptions}
          selectedKey={adType}
        />
      </div>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button variant={"contentOnly"} onPress={() => resetTable(table, "adReport")} fontSize={"xxlarge"}>
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <DataViewPicker viewKey="adReport" datasetKey="advertisement" table={table} />
            <SearchInput
              aria-label={t(($) => $.perf_reports["Search"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      {advByAdReportQuery.isError ?
        // Keep the toolbar above mounted so filters can still be edited or reset when the request fails.
        <div className={css({ padding: "small" })}>
          <DevErrorDisplay
            error={advByAdReportQuery.error}
            userMessage={t(($) => $.perf_reports["Error loading ad report."])}
          />
        </div>
      : <Table
          table={table}
          className={css({ minWidth: "100%" })}
          renderExpander={(row) => <AdvertisementExpander adId={row.original.ad_id} />}
        />
      }
      {userCanExportAds && (
        <HStack width={"100%"} padding={"small"}>
          <DownloadV4Report downloadMutation={exportMutation} />
        </HStack>
      )}
    </EditableTableWrapper>
  );
}
